import { ref } from 'vue';
import store from '@/store';

/**
 * Composable para tradução automática de texto usando Google Translate
 */
export default function useAutoTranslate() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Traduz texto automaticamente usando Google Translate API
   * @param {string} text - Texto a ser traduzido
   * @param {string} targetLang - Idioma de destino (pt, en, es)
   * @returns {Promise<string>} Texto traduzido
   */
  async function translateText(text, targetLang = null) {
    if (!text || text.trim() === '') {
      return text;
    }

    try {
      isLoading.value = true;
      error.value = null;

      // Se não especificar idioma, usar o idioma do usuário
      const userLang = targetLang || store.getters.getUserLang || 'pt';
      
      // Se o texto já está no idioma do usuário, retornar sem traduzir
      if (userLang === 'pt' && isPortuguese(text)) {
        return text;
      }
      if (userLang === 'en' && isEnglish(text)) {
        return text;
      }
      if (userLang === 'es' && isSpanish(text)) {
        return text;
      }

      const translatedText = await getGoogleTranslation(text, userLang);
      return translatedText;
    } catch (err) {
      error.value = err.message;
      console.error('Erro na tradução automática:', err);
      return text; // Retornar texto original em caso de erro
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Chama a API do Google Translate
   * @param {string} text - Texto a ser traduzido
   * @param {string} targetLang - Idioma de destino
   * @returns {Promise<string>} Texto traduzido
   */
  async function getGoogleTranslation(text, targetLang) {
    try {
      const uriText = encodeURIComponent(text);
      const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${targetLang}&dt=t&q=${uriText}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data && data[0] && data[0][0] && data[0][0][0]) {
        return data[0][0][0];
      }
      
      throw new Error('Resposta inválida da API de tradução');
    } catch (err) {
      throw new Error(`Erro na API de tradução: ${err.message}`);
    }
  }

  /**
   * Detecta se o texto está em português
   * @param {string} text - Texto para análise
   * @returns {boolean} True se for português
   */
  function isPortuguese(text) {
    const portugueseWords = ['com', 'para', 'que', 'uma', 'dos', 'das', 'pelo', 'pela', 'sobre', 'entre', 'desenvolvedor', 'experiência', 'trabalho', 'empresa', 'projeto', 'tecnologia', 'sistema', 'aplicação', 'desenvolvimento'];
    const words = text.toLowerCase().split(/\s+/);
    const portugueseCount = words.filter(word => portugueseWords.includes(word)).length;
    return portugueseCount > 0;
  }

  /**
   * Detecta se o texto está em inglês
   * @param {string} text - Texto para análise
   * @returns {boolean} True se for inglês
   */
  function isEnglish(text) {
    const englishWords = ['with', 'for', 'that', 'the', 'and', 'or', 'in', 'on', 'at', 'by', 'developer', 'experience', 'work', 'company', 'project', 'technology', 'system', 'application', 'development'];
    const words = text.toLowerCase().split(/\s+/);
    const englishCount = words.filter(word => englishWords.includes(word)).length;
    return englishCount > 0;
  }

  /**
   * Detecta se o texto está em espanhol
   * @param {string} text - Texto para análise
   * @returns {boolean} True se for espanhol
   */
  function isSpanish(text) {
    const spanishWords = ['con', 'para', 'que', 'una', 'los', 'las', 'por', 'sobre', 'entre', 'desarrollador', 'experiencia', 'trabajo', 'empresa', 'proyecto', 'tecnología', 'sistema', 'aplicación', 'desarrollo'];
    const words = text.toLowerCase().split(/\s+/);
    const spanishCount = words.filter(word => spanishWords.includes(word)).length;
    return spanishCount > 0;
  }

  /**
   * Formata texto para caixa alta (maiúsculas)
   * @param {string} text - Texto para formatação
   * @returns {string} Texto em maiúsculas
   */
  function formatToUppercase(text) {
    if (!text) return text;
    return text.toUpperCase();
  }

  /**
   * Traduz e formata texto para exibição
   * @param {string} text - Texto original
   * @param {string} targetLang - Idioma de destino
   * @param {boolean} uppercase - Se deve aplicar caixa alta
   * @returns {Promise<string>} Texto traduzido e formatado
   */
  async function translateAndFormat(text, targetLang = null, uppercase = true) {
    const translated = await translateText(text, targetLang);
    return uppercase ? formatToUppercase(translated) : translated;
  }

  return {
    isLoading,
    error,
    translateText,
    translateAndFormat,
    formatToUppercase,
    getGoogleTranslation
  };
}
